import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ClipboardList, ChevronRight, Plus } from 'lucide-react';
import type { IEmployeePolicyTableProps } from '@/@types/employee-detail';
import type { IPolicyFieldValue } from '@/@types/insurance';

const STATUS_VARIANTS: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  draft: 'secondary',
  active: 'default',
  expired: 'outline',
  cancelled: 'destructive',
};

function extractFieldValue(fieldValues: IPolicyFieldValue[], ...labels: string[]): string | undefined {
  const lowerLabels = labels.map(l => l.toLowerCase());
  return fieldValues.find(f => lowerLabels.some(l => f.fieldLabel.toLowerCase().includes(l)))?.value;
}

export function EmployeePolicyTable({ policies, onPolicyClick, onCreatePolicy }: IEmployeePolicyTableProps) {
  if (policies.length === 0) {
    return (
      <div className="rounded-xl border border-border bg-card p-8 text-center">
        <ClipboardList className="h-10 w-10 text-muted-foreground mx-auto mb-3" aria-hidden="true" />
        <p className="text-sm text-muted-foreground mb-4">No policies linked to this employee yet.</p>
        {onCreatePolicy && (
          <Button variant="outline" size="sm" onClick={onCreatePolicy}>
            <Plus className="h-4 w-4 mr-1" aria-hidden="true" />
            Create Policy
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Policy</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Start</TableHead>
            <TableHead>Expiry</TableHead>
            <TableHead>Premium</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="w-10" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {policies.map(policy => {
            const startDate = extractFieldValue(policy.fieldValues, 'start date');
            const expiryDate = extractFieldValue(policy.fieldValues, 'expiry date', 'end date');
            const premium = extractFieldValue(policy.fieldValues, 'premium', 'total premium');

            return (
              <TableRow
                key={policy._id}
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => onPolicyClick(policy._id)}
              >
                <TableCell>
                  <div className="flex items-center gap-2">
                    <ClipboardList className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden="true" />
                    <span className="font-medium">{policy.policyNumber}</span>
                  </div>
                </TableCell>
                <TableCell className="text-muted-foreground">{policy.insuranceTypeName ?? '—'}</TableCell>
                <TableCell className="text-muted-foreground">{startDate ?? '—'}</TableCell>
                <TableCell className="text-muted-foreground">{expiryDate ?? '—'}</TableCell>
                <TableCell className="text-muted-foreground">{premium ?? '—'}</TableCell>
                <TableCell>
                  <Badge variant={STATUS_VARIANTS[policy.status] ?? 'secondary'} className="capitalize">
                    {policy.status}
                  </Badge>
                </TableCell>
                <TableCell>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
